import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CircularProgress,
  InputBase,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { ErrorOutline } from "@mui/icons-material";
import { listOrganizations } from "../../api/adminApi";

const DEFAULT_STATUSES = new Set([
  "trial",
  "trialing",
  "active",
  "paid",
  "past_due",
]);

const STATUS_ORDER = [
  "trial",
  "trialing",
  "active",
  "paid",
  "past_due",
  "canceled",
  "cancelled",
  "unpaid",
  "null",
];

function normalizeStatus(status) {
  if (status == null || status === "") return "null";
  return String(status).toLowerCase();
}

function statusPillClass(status) {
  const s = normalizeStatus(status);
  if (s === "paid" || s === "active") {
    return "bg-emerald-50 text-emerald-700 border-emerald-200";
  }
  if (s === "canceled" || s === "cancelled" || s === "unpaid") {
    return "bg-red-50 text-red-700 border-red-200";
  }
  if (s === "past_due") {
    return "bg-orange-50 text-orange-700 border-orange-200";
  }
  if (s === "trial" || s === "trialing") {
    return "bg-sky-50 text-sky-700 border-sky-200";
  }
  return "bg-gray-50 text-gray-600 border-gray-200";
}

function formatStatusLabel(status) {
  const s = String(status || "").replace(/_/g, " ").trim();
  if (!s) return s;
  return s
    .split(/\s+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
}

function StatusPill({ status }) {
  if (status == null || status === "") {
    return <span className="text-gray-400">—</span>;
  }
  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap ${statusPillClass(
        status
      )}`}
    >
      {formatStatusLabel(status)}
    </span>
  );
}

function formatDate(value) {
  if (value == null || value === "") return "—";
  const n = typeof value === "number" ? value : Number(value);
  if (!Number.isFinite(n) || n === 0) {
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString();
  }
  // epoch seconds vs ms
  const ms = n < 1e12 ? n * 1000 : n;
  return new Date(ms).toLocaleDateString();
}

function formatMoney(mrr) {
  if (mrr == null || mrr === "") return "—";
  const n = Number(mrr);
  if (!Number.isFinite(n)) return "—";
  return `$${n.toFixed(0)}`;
}

function toSortableTime(value) {
  if (value == null || value === "") return 0;
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) {
    return n < 1e12 ? n * 1000 : n;
  }
  const ms = new Date(value).getTime();
  return Number.isFinite(ms) ? ms : 0;
}

function toSortableNumber(value) {
  if (value == null || value === "") return null;
  if (typeof value === "boolean") return value ? 1 : 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getSortValue(org, key) {
  switch (key) {
    case "organizationName":
      return (org.organizationName || "").toLowerCase();
    case "createdAt":
      return toSortableTime(org.createdAt);
    case "ownerEmail":
      return (org.ownerEmail || "").toLowerCase();
    case "subscriptionStatus":
      return (org.subscriptionStatus || "").toLowerCase();
    case "subscriptionEndDate":
      return toSortableTime(org.subscriptionEndDate);
    case "mrr":
      return toSortableNumber(org.mrr);
    case "twilioSetup":
      return toSortableNumber(org.twilioSetup);
    case "routeSetup":
      return toSortableNumber(org.routeSetup);
    case "userCount":
      return toSortableNumber(org.userCount);
    case "callsThisMonth":
      return toSortableNumber(org.callsThisMonth);
    case "callsLastMonth":
      return toSortableNumber(org.callsLastMonth);
    default:
      return "";
  }
}

function compareSortValues(a, b, dir) {
  const emptyA = a == null || a === "";
  const emptyB = b == null || b === "";
  if (emptyA && emptyB) return 0;
  if (emptyA) return 1;
  if (emptyB) return -1;

  let cmp = 0;
  if (typeof a === "number" && typeof b === "number") {
    cmp = a - b;
  } else {
    cmp = String(a).localeCompare(String(b), undefined, {
      numeric: true,
      sensitivity: "base",
    });
  }
  return dir === "asc" ? cmp : -cmp;
}

const TABLE_COLUMNS = [
  { key: "organizationName", label: "Organization" },
  { key: "createdAt", label: "Created" },
  { key: "ownerEmail", label: "Owner" },
  { key: "subscriptionStatus", label: "Status" },
  { key: "subscriptionEndDate", label: "Period end" },
  { key: "mrr", label: "MRR" },
  { key: "twilioSetup", label: "Twilio" },
  { key: "routeSetup", label: "Routes" },
  { key: "userCount", label: "Users" },
  { key: "callsThisMonth", label: "Calls (mo)" },
  { key: "callsLastMonth", label: "Calls (prev)" },
];

function extractOrgs(data) {
  if (!data) return [];
  if (Array.isArray(data.organizations)) return data.organizations;
  if (Array.isArray(data.items)) return data.items;
  if (Array.isArray(data.orgs)) return data.orgs;
  return [];
}

function extractCursor(data) {
  return (
    data?.lastEvaluatedKey ??
    data?.exclusiveStartKey ??
    data?.nextExclusiveStartKey ??
    null
  );
}

const AdminOrgList = () => {
  const navigate = useNavigate();
  const [orgs, setOrgs] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState(() => new Set(DEFAULT_STATUSES));
  const [sortKey, setSortKey] = useState("createdAt");
  const [sortDir, setSortDir] = useState("desc");

  const loadPage = useCallback(async (startKey = null, append = false) => {
    if (append) setLoadingMore(true);
    else {
      setLoading(true);
      setError(null);
    }
    try {
      const data = await listOrganizations({
        limit: 50,
        exclusiveStartKey: startKey,
      });
      const page = extractOrgs(data);
      setOrgs((prev) => (append ? [...prev, ...page] : page));
      setCursor(extractCursor(data));
      return extractCursor(data);
    } catch (err) {
      console.error("[Admin] list organizations failed", err);
      setError(
        err?.response?.data?.message ||
          err?.response?.data?.msg ||
          err?.message ||
          "Failed to load organizations"
      );
      return null;
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      let nextKey = await loadPage(null, false);
      while (nextKey && !cancelled) {
        setLoadingMore(true);
        try {
          const data = await listOrganizations({
            limit: 100,
            exclusiveStartKey: nextKey,
          });
          if (cancelled) return;
          const page = extractOrgs(data);
          setOrgs((prev) => [...prev, ...page]);
          nextKey = extractCursor(data);
          setCursor(nextKey);
        } catch (err) {
          if (!cancelled) {
            console.error("[Admin] list organizations (continue) failed", err);
          }
          break;
        } finally {
          if (!cancelled) setLoadingMore(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loadPage]);

  const statusOptions = useMemo(() => {
    const present = new Set(
      orgs.map((org) => normalizeStatus(org.subscriptionStatus))
    );
    const known = STATUS_ORDER.filter((status) => present.has(status));
    const extra = [...present]
      .filter((status) => !STATUS_ORDER.includes(status))
      .sort();
    return [...known, ...extra];
  }, [orgs]);

  const showingAll = statusFilter.size === 0;

  const toggleStatus = (status) => {
    setStatusFilter((prev) => {
      const next = new Set(prev);
      if (next.has(status)) next.delete(status);
      else next.add(status);
      return next;
    });
  };

  const showAllOrgs = () => {
    setStatusFilter(new Set());
  };

  const resetDefaultFilters = () => {
    setStatusFilter(new Set(DEFAULT_STATUSES));
  };

  const stats = useMemo(() => {
    let totalMrr = 0;
    let activeCustomers = 0;
    let trials = 0;

    for (const org of orgs) {
      const status = normalizeStatus(org.subscriptionStatus);
      const mrr = Number(org.mrr);
      if (
        (status === "paid" || status === "active" || status === "past_due") &&
        Number.isFinite(mrr)
      ) {
        totalMrr += mrr;
      }
      if (status === "paid" || status === "active") {
        activeCustomers += 1;
      }
      if (status === "trial" || status === "trialing") {
        trials += 1;
      }
    }

    return { totalMrr, activeCustomers, trials };
  }, [orgs]);

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortDir((prev) => (prev === "asc" ? "desc" : "asc"));
      return;
    }
    setSortKey(key);
    // Dates/numbers default to desc; text to asc
    const numericKeys = new Set([
      "createdAt",
      "subscriptionEndDate",
      "mrr",
      "userCount",
      "callsThisMonth",
      "callsLastMonth",
      "twilioSetup",
      "routeSetup",
    ]);
    setSortDir(numericKeys.has(key) ? "desc" : "asc");
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return orgs
      .filter((org) => {
        const status = normalizeStatus(org.subscriptionStatus);
        if (statusFilter.size > 0 && !statusFilter.has(status)) return false;
        if (!q) return true;
        const hay = [
          org.organizationName,
          org.orgId,
          org.ownerEmail,
          org.subscriptionStatus,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        return hay.includes(q);
      })
      .sort((a, b) =>
        compareSortValues(
          getSortValue(a, sortKey),
          getSortValue(b, sortKey),
          sortDir
        )
      );
  }, [orgs, search, statusFilter, sortKey, sortDir]);

  return (
    <div>
      <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4 mb-5">
        <div>
          <h1 className="lg:text-[22px] text-[20px] font-bold text-gray-900">
            Organizations
          </h1>
          <p className="text-[13px] text-gray-500 mt-1">
            {filtered.length} shown
            {orgs.length !== filtered.length ? ` of ${orgs.length} loaded` : ""}
            {loadingMore ? " · loading remaining…" : ""}
          </p>
        </div>
        <InputBase
          placeholder="Search name, email, org id…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            bgcolor: "white",
            border: "1px solid #E0E0E0",
            px: 2,
            py: 1,
            fontSize: "14px",
            borderRadius: "8px",
            minWidth: { xs: "100%", lg: 320 },
          }}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-4">
        <div className="bg-white border border-gray-200 rounded-[10px] px-4 py-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
            Total MRR
          </div>
          <div className="text-[22px] font-bold text-gray-900 mt-1">
            ${stats.totalMrr.toLocaleString(undefined, {
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
            })}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-[10px] px-4 py-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
            Active customers
          </div>
          <div className="text-[22px] font-bold text-gray-900 mt-1">
            {stats.activeCustomers.toLocaleString()}
          </div>
        </div>
        <div className="bg-white border border-gray-200 rounded-[10px] px-4 py-3">
          <div className="text-[11px] uppercase tracking-wide text-gray-400 font-semibold">
            Trials
          </div>
          <div className="text-[22px] font-bold text-gray-900 mt-1">
            {stats.trials.toLocaleString()}
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-[10px] p-3 mb-4 flex flex-wrap items-center gap-2">
        <button
          type="button"
          onClick={showingAll ? resetDefaultFilters : showAllOrgs}
          className={`text-[12px] px-2.5 py-1 rounded-md border font-semibold ${
            showingAll
              ? "bg-[rgba(96,91,255,0.1)] border-[#605BFF] text-[#605BFF]"
              : "bg-white border-gray-200 text-gray-700 hover:border-gray-300"
          }`}
        >
          {showingAll ? "Default filters" : "Show all orgs"}
        </button>
        <div className="flex flex-wrap gap-1.5 ml-0 lg:ml-1">
          {statusOptions.map((status) => {
            const active = statusFilter.has(status);
            return (
              <button
                key={status}
                type="button"
                onClick={() => toggleStatus(status)}
                className={`text-[12px] px-2.5 py-1 rounded-md border ${
                  active
                    ? "bg-[rgba(96,91,255,0.1)] border-[#605BFF] text-[#605BFF] font-semibold"
                    : "bg-white border-gray-200 text-gray-500"
                }`}
              >
                {status}
              </button>
            );
          })}
        </div>
      </div>

      {error && (
        <div className="text-red-500 text-[13px] font-semibold mb-3">
          <ErrorOutline sx={{ fontSize: 16, mr: 0.5 }} />
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-16">
          <CircularProgress size={32} sx={{ color: "#605BFF" }} />
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-[10px] overflow-x-auto">
          <Table size="small">
            <TableHead>
              <TableRow>
                {TABLE_COLUMNS.map((col) => {
                  const active = sortKey === col.key;
                  return (
                    <TableCell
                      key={col.key}
                      onClick={() => handleSort(col.key)}
                      sx={{
                        fontWeight: 600,
                        fontSize: 12,
                        color: active ? "#605BFF" : "#6B7280",
                        whiteSpace: "nowrap",
                        cursor: "pointer",
                        userSelect: "none",
                        "&:hover": { color: "#605BFF" },
                      }}
                    >
                      <span className="inline-flex items-center gap-1">
                        {col.label}
                        <span className="text-[10px] opacity-80">
                          {active ? (sortDir === "asc" ? "↑" : "↓") : ""}
                        </span>
                      </span>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} sx={{ py: 4, textAlign: "center", color: "#9CA3AF" }}>
                    No organizations match the current filters.
                  </TableCell>
                </TableRow>
              ) : (
                filtered.map((org) => (
                  <TableRow
                    key={org.orgId}
                    hover
                    sx={{ cursor: "pointer" }}
                    onAuxClick={(e) => {
                      if (e.button === 1) {
                        window.open(`/admin/orgs/${org.orgId}`, "_blank");
                      }
                    }}
                    onClick={(e) =>
                      e.metaKey || e.ctrlKey
                        ? window.open(`/admin/orgs/${org.orgId}`, "_blank")
                        : navigate(`/admin/orgs/${org.orgId}`, {
                            state: {
                              ownerEmail: org.ownerEmail || null,
                              organizationName: org.organizationName || null,
                              subscriptionStatus: org.subscriptionStatus || null,
                              subscriptionEndDate: org.subscriptionEndDate || null,
                              mrr: org.mrr ?? null,
                              paymentMethodEnabled: org.paymentMethodEnabled ?? null,
                              stripeCustomerId: org.stripeCustomerId || null,
                              stripeSubscriptionId: org.stripeSubscriptionId || null,
                              trialExpiresAt: org.trialExpiresAt ?? null,
                            },
                          })
                    }
                  >
                    <TableCell sx={{ fontSize: 13, fontWeight: 600 }}>
                      <div>{org.organizationName || "—"}</div>
                      <div className="text-[11px] text-gray-400 font-normal">
                        {org.orgId}
                      </div>
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, whiteSpace: "nowrap" }}>
                      {formatDate(org.createdAt)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{org.ownerEmail || "—"}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      <div className="flex items-center gap-1 flex-wrap">
                        <StatusPill status={org.subscriptionStatus} />
                        {org.lockoutOverrideActive ? (
                          <span
                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold border whitespace-nowrap bg-indigo-50 text-indigo-700 border-indigo-200"
                            title={
                              org.lockoutOverrideExpiration
                                ? `Until ${formatDate(org.lockoutOverrideExpiration)}`
                                : "Lockout override active"
                            }
                          >
                            Override
                          </span>
                        ) : null}
                      </div>
                    </TableCell>
                    <TableCell sx={{ fontSize: 13, whiteSpace: "nowrap" }}>
                      {formatDate(org.subscriptionEndDate)}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>{formatMoney(org.mrr)}</TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {org.twilioSetup == null ? "—" : org.twilioSetup ? "Yes" : "No"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {org.routeSetup == null ? "—" : org.routeSetup ? "Yes" : "No"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {org.userCount ?? "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {org.callsThisMonth ?? "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13 }}>
                      {org.callsLastMonth ?? "—"}
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      )}

      {loadingMore && (
        <div className="flex justify-center items-center gap-2 mt-4 text-[13px] text-gray-500">
          <CircularProgress size={16} sx={{ color: "#605BFF" }} />
          Loading remaining organizations…
        </div>
      )}
    </div>
  );
};

export default AdminOrgList;
